import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserData, useAction, addActions } from '@/lib/firebase/firestore';
import { ActionResult, MachineType } from '@/types';
import { useToast } from '@/hooks/use-sonner-toast';
import { type ToastProps, type ToastVariant } from '@/components/ui/toast';

// Define the context type
interface ActionContextType {
  actionsRemaining: number;
  actionsUsed: number;
  isPremium: boolean;
  isLoading: boolean;
  useGameAction: (
    actionType: 'combine' | 'goal' | 'reset',
    machineType?: MachineType
  ) => Promise<ActionResult>;
  addUserActions: (count: number, paymentId?: string, amount?: number) => Promise<boolean>;
  resetActionsUsed: () => void;
}

// Create the context with default values
const ActionContext = createContext<ActionContextType>({
  actionsRemaining: 50,
  actionsUsed: 0,
  isPremium: false,
  isLoading: true,
  useGameAction: async () => ({ success: false }),
  addUserActions: async () => false,
  resetActionsUsed: () => {},
});

// Custom hook to use the context
export const useActions = () => useContext(ActionContext);

// Provider component
export const ActionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [actionsRemaining, setActionsRemaining] = useState<number>(50);
  const [actionsUsed, setActionsUsed] = useState<number>(0);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load user data when the user changes
  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);

      if (currentUser) {
        try {
          // Add a small delay to ensure the user document is fully created
          // This helps with the race condition where the document might not be fully initialized
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Try up to 3 times to get user data with all required fields
          let userData = null;
          let attempts = 0;

          while (!userData?.actionsRemaining && attempts < 3) {
            attempts++;
            

            userData = await getUserData(currentUser.uid);

            if (userData?.actionsRemaining && userData?.isPremium !== undefined) {
              
              break;
            } else if (attempts < 3) {
              
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }

          if (userData && userData.actionsRemaining !== undefined) {
            
            setActionsRemaining(userData.actionsRemaining);
            setIsPremium(!!userData.isPremium);
            // Reset actions used counter for logged-in users on page load
            setActionsUsed(0);
          } else {
            
            // Default values for new users
            setActionsRemaining(50);
            setIsPremium(false);
          }
        } catch (error) {
          
          // Default values on error
          setActionsRemaining(50);
          setIsPremium(false);
        }
      } else {
        // No user is logged in, set default values
        setActionsRemaining(0);
        setActionsUsed(0);
        setIsPremium(false);
      }

      setIsLoading(false);
    };

    loadUserData();
  }, [currentUser]);

  // Use an action
  const useGameAction = async (
    actionType: 'combine' | 'goal' | 'reset',
    machineType?: MachineType
  ): Promise<ActionResult> => {
    // Require user to be logged in
    if (!currentUser) {
      toast({
        title: 'Login Required',
        description: 'You must be signed in to use the forge.',
        variant: 'destructive',
      });
      return {
        success: false,
        message: 'Login required to use the forge.',
        needsPayment: false,
      };
    }

    // For logged-in users
    try {
      // Check if the user has actions remaining
      if (actionsRemaining <= 0) {
        toast({
          title: 'No Actions Remaining',
          description: 'Purchase more actions to continue playing.',
          variant: 'destructive',
        });
        return {
          success: false,
          message: 'No actions remaining. Purchase more to continue.',
          needsPayment: true,
        };
      }

      // Use an action
      const success = await useAction(currentUser.uid);

      if (success) {
        // Update local state, ensuring we don't go below 0
        setActionsRemaining((prev) => Math.max(0, prev - 1));

        // Check if this was the last action
        if (actionsRemaining <= 1) {
          toast({
            title: 'Last Action Used',
            description: 'Purchase more actions to continue playing.',
            variant: 'warning',
          });
        }

        return {
          success: true,
          actionsRemaining: Math.max(0, actionsRemaining - 1),
        };
      } else {
        toast({
          title: 'Action Failed',
          description: 'There was an error using your action. Please try again.',
          variant: 'destructive',
        });
        return {
          success: false,
          message: 'Error using action. Please try again.',
        };
      }
    } catch (error) {
      
      toast({
        title: 'Action Failed',
        description: 'There was an error using your action. Please try again.',
        variant: 'destructive',
      });
      return {
        success: false,
        message: 'Error using action. Please try again.',
      };
    }
  };

  // Add actions to the user's account
  const addUserActions = async (
    count: number,
    paymentId?: string,
    amount?: number
  ): Promise<boolean> => {
    if (!currentUser) {
      toast({
        title: 'Not Signed In',
        description: 'You must be signed in to add actions.',
        variant: 'destructive',
      });
      return false;
    }

    try {
      const success = await addActions(currentUser.uid, count, paymentId, amount);

      if (success) {
        // Update local state
        setActionsRemaining((prev) => prev + count);
        setIsPremium(true);

        toast({
          title: 'Actions Added',
          description: `${count} actions have been added to your account.`,
          variant: 'success',
        });

        return true;
      } else {
        toast({
          title: 'Failed to Add Actions',
          description: 'There was an error adding actions to your account. Please try again.',
          variant: 'destructive',
        });
        return false;
      }
    } catch (error) {
      
      toast({
        title: 'Failed to Add Actions',
        description: 'There was an error adding actions to your account. Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Simple function to reset actions used counter if needed
  const resetActionsUsed = () => {
    setActionsUsed(0);
  };

  const value = {
    actionsRemaining,
    actionsUsed,
    isPremium,
    isLoading,
    useGameAction,
    addUserActions,
    resetActionsUsed,
  };

  return <ActionContext.Provider value={value}>{children}</ActionContext.Provider>;
};

